import { injectable } from 'inversify';
import { areIntervalsOverlapping } from 'date-fns';

import { Calendar } from '@vk-hr-tek/core/calendar';
import { CalendarItemColor, Range } from '@vk-hr-tek/ui/Calendar';
import { FormatService } from '@vk-hr-tek/core/format';

import {
  AbsenceEmployee,
  AbsenceListGroup,
  AbsenceItem,
  Unit,
  AbsenceActiveNode,
} from '@app/gen/absences';

import { AbsenceGroupType, AbsenceItemType } from '../types';

type ActionTypes =
  | 'generate_document_from_template'
  | 'upload'
  | 'sign'
  | 'accept';

@injectable()
export class AbsencesMapper {
  constructor(private calendar: Calendar, private format: FormatService) {}

  processEmployee(employee: AbsenceEmployee, units?: Unit[]) {
    return {
      type: 'group',
      id: employee.unit?.id,
      name: employee.unit?.name,
      personalNumber: employee.personnel_number,
      position: employee.position,
      structure: this.processGroupStructure(units, units?.[0]?.id),
      supervisor: this.processGroupSupervisor(employee.unit),
      employeeId: employee.id,
    };
  }

  processGroupStructure(units?: Unit[], rootId?: string): string[] {
    if (!units || !rootId) {
      return [];
    }

    const result: string[] = [];
    let nextId: string | undefined = rootId;

    const unitsMap = units?.reduce((acc: Record<string, Unit>, curr) => {
      acc[curr.id] = curr;

      return acc;
    }, {});

    while (nextId && unitsMap[nextId]) {
      result.push(unitsMap[nextId].name);
      nextId = unitsMap[nextId].parent_id;
    }

    return result.reverse();
  }

  processGroupSupervisor(unit?: Unit) {
    if (!unit || !unit.manager) {
      return;
    }

    const name = [
      unit.manager?.last_name || '',
      unit.manager?.first_name || '',
      unit.manager?.middle_name || '',
    ]
      .filter((el) => el)
      .join(' ');

    return {
      name,
      personalNumber: unit.manager?.personnel_number || '',
    };
  }

  private processActiveNodes(activeNodes: AbsenceActiveNode[] | undefined) {
    if (!activeNodes) return [];

    return activeNodes
      .reduce(
        (
          acc: {
            type: ActionTypes;
            deadline: string;
            isMultiple: boolean;
          }[],
          curr,
        ) => {
          if (!curr.deadline || !curr.actions) {
            return acc;
          }

          curr.actions?.forEach((action) => {
            const existingTypeIdx = acc.findIndex(
              (actionItem) => actionItem.type === action,
            );

            if (existingTypeIdx === -1) {
              const actionObj = {
                isMultiple: false,
                type: action,
                deadline: curr.deadline as string,
              };

              acc.push(actionObj);
            } else {
              acc[existingTypeIdx].isMultiple = true;
              const accDeadline = new Date(acc[existingTypeIdx].deadline);
              const currDeadline = new Date(curr.deadline as string);

              if (currDeadline < accDeadline) {
                acc[existingTypeIdx].deadline = curr.deadline as string;
              }
            }
          });

          return acc;
        },
        [],
      )
      .map((action) => {
        const deadlineDate = new Date(action.deadline);
        deadlineDate.setDate(deadlineDate.getDate() - 1);

        return {
          ...action,
          deadline: this.format.toDate(deadlineDate.toDateString()),
        };
      });
  }

  private processRangeItem(absence: AbsenceItem) {
    let color: CalendarItemColor = 'violet';

    if (
      absence.type_group === 'Отпуск' ||
      absence.type_group === 'Декретный отпуск' ||
      absence.type_group === 'Учебный отпуск'
    )
      color = 'blue';
    else if (absence.type_group === 'Командировка') color = 'orange';
    else if (absence.type_group === 'Больничный') color = 'purple';

    const start = new Date(absence.from_date);
    const end = new Date(absence.to_date);

    return {
      id: absence.type,
      eventId: absence.event_id || '',
      start,
      end,
      tooltip: absence.type_group ?? absence.type,
      color,
      actions: this.processActiveNodes(absence.active_nodes),
      status: absence.status,
      count: absence.days_count,
      period: `С ${this.format.toDate(
        absence.from_date,
      )} по ${this.format.toDate(absence.to_date)}`,
    };
  }

  private processRanges(absences: AbsenceItem[]) {
    const ranges: Range[] = absences
      .map((range) => this.processRangeItem(range))
      .sort((range1, range2) => {
        if (this.calendar.isSameDay(range1.end, range2.end)) {
          return range1.start > range2.start ? 1 : -1;
        } else {
          return range1.end > range2.end ? 1 : -1;
        }
      });

    let i = 0;
    const result: {
      start: Date;
      end: Date;
      ranges: Range[];
    }[] = [];

    while (i < ranges.length) {
      const currentRange = ranges[i];
      const start = currentRange.start;
      let end = currentRange.end;
      const formattedRanges = [currentRange];

      let j = i + 1;

      while (
        ranges[j] &&
        (areIntervalsOverlapping(
          { start, end },
          { start: ranges[j].start, end: ranges[j].end },
        ) ||
          this.calendar.isSameDay(end, ranges[j].start))
      ) {
        end = ranges[j].end;
        formattedRanges.push(ranges[j]);
        j++;
        i = j - 1;
      }

      result.push({
        start,
        end,
        ranges: formattedRanges,
      });
      i++;
    }

    return result;
  }

  processEmployeeAbsences(
    absences: AbsenceItem[],
    employee: AbsenceEmployee,
    employeeType: 'self' | 'colleague' | 'manager' | undefined,
  ) {
    if (!absences.length) {
      return [
        {
          type: 'item',
          name: {
            fullName: employee.name,
          },
          eventId: '',
          tooltip: '',
          employeeId: employee.id,
          ranges: [],
          calendarDiff: this.processEmployeeCalendarDiff(employee),
          employeeType,
        },
      ];
    }

    return [
      {
        type: 'item',
        name: {
          fullName: employee.name,
        },
        tooltip: '',
        employeeId: employee.id,
        eventId: '',
        ranges: this.processRanges(absences),
        calendarDiff: this.processEmployeeCalendarDiff(employee),
        employeeType,
      },
    ];
  }

  processCompanyAbsences(absences: AbsenceItem[], employee: AbsenceEmployee) {
    if (!absences.length) {
      return [
        {
          type: 'item',
          name: {
            fullName: employee.name,
          },
          eventId: '',
          tooltip: '',
          employeeId: employee.id,
          ranges: [],
          calendarDiff: this.processEmployeeCalendarDiff(employee),
        },
      ];
    }

    return [
      {
        type: 'item',
        name: {
          fullName: employee.name,
        },
        tooltip: '',
        employeeId: employee.id,
        eventId: '',
        ranges: this.processRanges(absences),
        calendarDiff: this.processEmployeeCalendarDiff(employee),
      },
    ];
  }

  processAbsenceClosestTask(groupes: AbsenceListGroup[]) {
    return groupes
      .reduce((acc: AbsenceItem[] | [], { absences }) => {
        return [...acc, ...absences];
      }, [])
      .sort((a, b) => {
        return a.from_date > b.from_date ? 1 : -1;
      })
      .filter(
        (item) =>
          !item.event_id &&
          item.is_scheduled_vacation &&
          item.status === 'new' &&
          new Date() < new Date(item.from_date),
      )
      .map((item) => {
        return { ...item, type: item.type };
      })[0];
  }

  processEmployeeGroups(groups: AbsenceListGroup[]) {
    const employeeGroups = groups.reduce(
      (
        acc: (AbsenceItemType | AbsenceGroupType)[],
        { type: employeeType, employee, absences, units },
      ) => {
        if (employeeType !== 'manager') {
          const group = this.processEmployee(employee, units);

          const groupAlreadyInData = acc.find(
            (item) =>
              item.type === group.type &&
              (item as AbsenceGroupType).id === group.id,
          );

          if (!groupAlreadyInData) {
            acc.push(group);
          }
        }

        acc.push(
          ...this.processEmployeeAbsences(absences, employee, employeeType),
        );

        return acc;
      },
      [],
    );

    return employeeGroups;
  }

  processCompanyGroups(groups: AbsenceListGroup[]) {
    const companyGroups = groups.reduce(
      (
        acc: (AbsenceItemType | AbsenceGroupType)[],
        { employee, absences, units },
      ) => {
        const group: AbsenceGroupType = this.processEmployee(employee, units);

        const groupAlreadyInData = acc.find(
          (item) =>
            item.type === group.type &&
            (item as AbsenceGroupType).id === group.id,
        );

        if (!groupAlreadyInData) {
          acc.push(group);
        }

        acc.push(...this.processCompanyAbsences(absences, employee));

        return acc;
      },
      [],
    );

    return companyGroups;
  }

  processEmployeeCalendarDiff(employee: AbsenceEmployee) {
    return (employee.calendar_diff || []).reduce(
      (acc: Record<string, 'workday' | 'weekend' | 'holiday'>, curr) => {
        acc[curr.date] = curr.type;

        return acc;
      },
      {},
    );
  }
}
